(function (global, factory) {
	typeof exports === "object" && typeof module !== "undefined"
		? (module.exports = factory())
		: typeof define === "function" && define.amd
			? define(factory)
			: ((global =
					typeof globalThis !== "undefined" ? globalThis : global || self),
				(global.ScrollTrigger = factory()));
})(this, function () {
	"use strict";

	/**
	 * ScrollTrigger - Scroll spy plugin for tracking section visibility
	 * https://github.com/magic-spells/scroll-trigger
	 *
	 * Monitors when sections cross a configurable trigger line in the viewport
	 * (measured from the bottom) and provides callbacks for navigation synchronization.
	 *
	 * @example
	 * const trigger = new ScrollTrigger({
	 *   sections: '.collection-section',
	 *   offset: 100, // 100px from bottom of viewport
	 *   onIndexChange: ({ currentIndex, currentElement }) => {
	 *     console.log('Active section:', currentIndex);
	 *   }
	 * });
	 */
	class ScrollTrigger {
		// Private fields
		#elements = [];
		#currentIndex = -1;
		#observer = null;
		#config = {
			offset: 100,
			threshold: 0.1,
			throttle: 50,
			behavior: "smooth",
			onIndexChange: null,
		};
		#intersectingMap = new Map();
		#throttleTimer = null;
		#isDestroyed = false;
		#resizeObserver = null;
		#scrollHandler = null;

		/**
		 * Create a new ScrollTrigger instance
		 * @param {Object} options - Configuration options
		 * @param {string|NodeList|Array} options.sections - Sections to track (CSS selector, NodeList, or Array of elements)
		 * @param {number|string} [options.offset=100] - Distance from bottom of viewport to trigger active state (px or percentage like '20%')
		 * @param {number} [options.threshold=0.1] - IntersectionObserver threshold (0-1)
		 * @param {number} [options.throttle=100] - Throttle delay for scroll events (ms)
		 * @param {string} [options.behavior='smooth'] - Scroll behavior ('smooth' or 'auto')
		 * @param {Function} [options.onIndexChange] - Callback when active section changes (receives object: { currentIndex, previousIndex, currentElement, previousElement })
		 */
		constructor(options = {}) {
			// Merge config
			this.#config = { ...this.#config, ...options };

			// Get elements
			this.#elements = this.#getElements(this.#config.sections);

			if (this.#elements.length === 0) {
				console.warn("ScrollTrigger: No elements found");
				return;
			}

			// Initialize observer
			this.#setupObserver();

			// Watch for viewport resize if any element uses percentage offset
			if (this.#hasPercentageOffsets()) {
				this.#setupResizeObserver();
			}

			// Add scroll listener as fallback for mobile
			this.#setupScrollListener();
		}

		/**
		 * Get elements from various input types
		 */
		#getElements(input) {
			if (typeof input === "string") {
				return Array.from(document.querySelectorAll(input));
			} else if (input instanceof NodeList) {
				return Array.from(input);
			} else if (Array.isArray(input)) {
				return input;
			}
			return [];
		}

		/**
		 * Check if offset is a percentage value
		 */
		#isPercentageOffset(offset) {
			return typeof offset === "string" && offset.includes("%");
		}

		/**
		 * Calculate pixel offset from bottom of viewport
		 * Converts percentage or pixel value to pixels from bottom
		 */
		#calculateOffset(offset) {
			if (this.#isPercentageOffset(offset)) {
				const percentage = parseFloat(offset) / 100;
				return Math.round(window.innerHeight * percentage);
			}
			return typeof offset === "number" ? offset : 100;
		}

		/**
		 * Get the offset for a specific element (custom or default)
		 * Checks for data-animate-offset attribute, falls back to config
		 */
		#getElementOffset(element) {
			if (!element) return this.#config.offset;

			const customOffset = element.getAttribute("data-animate-offset");
			if (customOffset !== null) {
				// Parse as number if it's just digits, otherwise return as string (for percentages)
				return /^\d+$/.test(customOffset)
					? parseInt(customOffset, 10)
					: customOffset;
			}

			return this.#config.offset;
		}

		/**
		 * Check if any element (or config) uses a percentage offset
		 */
		#hasPercentageOffsets() {
			// Check global config
			if (this.#isPercentageOffset(this.#config.offset)) {
				return true;
			}

			// Check if any element has a custom percentage offset
			return this.#elements.some((element) => {
				const customOffset = element.getAttribute("data-animate-offset");
				return customOffset && this.#isPercentageOffset(customOffset);
			});
		}

		/**
		 * Setup ResizeObserver to handle viewport changes with percentage offsets
		 */
		#setupResizeObserver() {
			this.#resizeObserver = new ResizeObserver(() => {
				// Recreate observer with new calculated offset
				if (this.#observer) {
					this.#observer.disconnect();
				}
				this.#setupObserver();
			});

			this.#resizeObserver.observe(document.documentElement);
		}

		/**
		 * Setup scroll listener as fallback for mobile browsers
		 * IntersectionObserver can miss events during momentum scrolling
		 */
		#setupScrollListener() {
			this.#scrollHandler = () => {
				this.#throttleIndexUpdate();
			};

			window.addEventListener("scroll", this.#scrollHandler, { passive: true });
		}

		/**
		 * Setup IntersectionObserver to track sections
		 */
		#setupObserver() {
			// Calculate pixel offset from bottom (handles both px and %)
			const offsetPx = this.#calculateOffset(this.#config.offset);

			// Create observer with offset from bottom
			// Top margin removes everything above, bottom margin creates trigger zone
			const rootMargin = `0px 0px -${offsetPx}px 0px`;

			this.#observer = new IntersectionObserver(
				(entries) => this.#handleIntersection(entries),
				{
					root: null,
					rootMargin: rootMargin,
					threshold: this.#config.threshold,
				},
			);

			// Observe all elements
			this.#elements.forEach((element) => {
				this.#observer.observe(element);
				this.#intersectingMap.set(element, false);
			});
		}

		/**
		 * Handle intersection events
		 */
		#handleIntersection(entries) {
			if (this.#isDestroyed) return;

			// Update intersecting map
			entries.forEach((entry) => {
				this.#intersectingMap.set(entry.target, entry.isIntersecting);
			});

			// Throttle index calculation
			this.#throttleIndexUpdate();
		}

		/**
		 * Throttle index updates to prevent excessive calls
		 */
		#throttleIndexUpdate() {
			if (this.#throttleTimer) return;

			this.#throttleTimer = setTimeout(() => {
				this.#updateActiveIndex();
				this.#throttleTimer = null;
			}, this.#config.throttle);
		}

		/**
		 * Update the active index based on intersecting elements
		 * Supports per-element custom offsets via data-animate-offset attribute
		 */
		#updateActiveIndex() {
			if (this.#isDestroyed) return;

			// Check each element against its custom offset
			let newIndex = -1;

			// Find the closest element that has crossed its trigger line
			// Check from bottom to top to find the last one that crossed
			for (let i = this.#elements.length - 1; i >= 0; i--) {
				const element = this.#elements[i];
				const elementOffset = this.#getElementOffset(element);
				const offsetPx = this.#calculateOffset(elementOffset);
				const triggerLine = window.innerHeight - offsetPx;

				const rect = element.getBoundingClientRect();

				// If this element's top is at or above its trigger line, it's active
				if (rect.top <= triggerLine) {
					newIndex = i;
					break;
				}
			}

			// Fire callback if index changed
			if (newIndex !== this.#currentIndex) {
				const previousIndex = this.#currentIndex;
				this.#currentIndex = newIndex;

				const currentElement = this.#elements[newIndex] || null;
				const previousElement = this.#elements[previousIndex] || null;

				if (
					this.#config.onIndexChange &&
					typeof this.#config.onIndexChange === "function"
				) {
					this.#config.onIndexChange({
						currentIndex: newIndex,
						previousIndex,
						currentElement,
						previousElement,
					});
				}

				// Emit custom event
				this.#emitEvent("scroll-trigger:change", {
					index: newIndex,
					previousIndex,
					section: currentElement,
					previousSection: previousElement,
				});
			}
		}

		/**
		 * Emit custom event
		 */
		#emitEvent(eventName, detail) {
			if (typeof window !== "undefined") {
				window.dispatchEvent(
					new CustomEvent(eventName, {
						bubbles: true,
						detail: detail,
					}),
				);
			}
		}

		/**
		 * Get the current active index
		 * @returns {number} Current active element index (-1 if none)
		 */
		getCurrentIndex() {
			return this.#currentIndex;
		}

		/**
		 * Get the current active element
		 * @returns {Element|null} Current active element or null
		 */
		getCurrentElement() {
			return this.#elements[this.#currentIndex] || null;
		}

		/**
		 * Get all tracked elements
		 * @returns {Array<Element>} Array of tracked elements
		 */
		getElements() {
			return [...this.#elements];
		}

		/**
		 * Scroll to a specific element by index
		 * @param {number} index - Index of element to scroll to
		 * @param {Object} [options] - Scroll options
		 * @param {string} [options.behavior] - Scroll behavior ('smooth' or 'auto')
		 * @param {number} [options.offset] - Additional offset in pixels (positive = element appears higher, negative = element appears lower)
		 */
		scrollToIndex(index, options = {}) {
			if (index < 0 || index >= this.#elements.length) {
				console.warn(`ScrollTrigger: Invalid index ${index}`);
				return;
			}

			const element = this.#elements[index];
			const behavior = options.behavior || this.#config.behavior;
			const additionalOffset = options.offset || 0;

			// Calculate offset from bottom in pixels (respects element's custom offset)
			const elementOffset = this.#getElementOffset(element);
			const offsetPx = this.#calculateOffset(elementOffset);
			const triggerLine = window.innerHeight - offsetPx;

			const rect = element.getBoundingClientRect();
			const scrollTop =
				window.pageYOffset || document.documentElement.scrollTop;
			const targetPosition =
				rect.top + scrollTop - triggerLine - additionalOffset;

			scrollWindowTo(targetPosition, behavior, {
				duration: options.duration,
				easing: options.easing,
			});
		}

		/**
		 * Scroll to a specific element
		 * @param {Element} element - Element to scroll to
		 * @param {Object} [options] - Scroll options (see scrollToIndex)
		 */
		scrollToElement(element, options = {}) {
			const index = this.#elements.indexOf(element);
			if (index === -1) {
				console.warn("ScrollTrigger: Element not found in tracked elements");
				return;
			}
			this.scrollToIndex(index, options);
		}

		/**
		 * Recalculate element positions (call if DOM changes)
		 */
		refresh() {
			if (this.#isDestroyed) return;

			// Re-observe elements to update positions
			this.#elements.forEach((element) => {
				this.#observer.unobserve(element);
				this.#observer.observe(element);
			});

			// Force index update
			this.#updateActiveIndex();
		}

		/**
		 * Update configuration
		 * @param {Object} newConfig - New configuration options
		 */
		updateConfig(newConfig = {}) {
			if (this.#isDestroyed) return;

			const needsObserverUpdate =
				("offset" in newConfig && newConfig.offset !== this.#config.offset) ||
				("threshold" in newConfig &&
					newConfig.threshold !== this.#config.threshold);

			this.#config = { ...this.#config, ...newConfig };

			// Handle resize observer for percentage changes
			if ("offset" in newConfig) {
				const hasPercentages = this.#hasPercentageOffsets();

				if (hasPercentages && !this.#resizeObserver) {
					this.#setupResizeObserver();
				} else if (!hasPercentages && this.#resizeObserver) {
					this.#resizeObserver.disconnect();
					this.#resizeObserver = null;
				}
			}

			if (needsObserverUpdate) {
				if (this.#observer) {
					this.#observer.disconnect();
				}
				this.#setupObserver();
			}
		}

		/**
		 * Destroy the tracker and cleanup
		 */
		destroy() {
			if (this.#isDestroyed) return;

			this.#isDestroyed = true;

			// Clear throttle timer
			if (this.#throttleTimer) {
				clearTimeout(this.#throttleTimer);
				this.#throttleTimer = null;
			}

			// Disconnect observer
			if (this.#observer) {
				this.#observer.disconnect();
				this.#observer = null;
			}

			// Disconnect resize observer
			if (this.#resizeObserver) {
				this.#resizeObserver.disconnect();
				this.#resizeObserver = null;
			}

			// Remove scroll listener
			if (this.#scrollHandler) {
				window.removeEventListener("scroll", this.#scrollHandler);
				this.#scrollHandler = null;
			}

			// Clear maps and arrays
			this.#intersectingMap.clear();
			this.#elements = [];
			this.#currentIndex = -1;
		}
	}

	return ScrollTrigger;
});

/**
 * Smoothly animates the window scroll position using requestAnimationFrame.
 * Avoids Safari bugs with native smooth scrolling by providing a custom implementation.
 * @param {number} targetY - The destination scroll position (in pixels from top of document)
 * @param {Object} [options]
 * @param {number} [options.duration=500] - Animation duration in ms
 * @param {Function} [options.easing] - Custom easing function receiving progress (0-1)
 */
const animateWindowScrollTo = (() => {
	const defaultDuration = 500;
	const defaultEasing = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
	let activeFrame = null;

	return function (targetY, options = {}) {
		const duration =
			typeof options.duration === "number" && options.duration >= 0
				? options.duration
				: defaultDuration;
		const easing =
			typeof options.easing === "function" ? options.easing : defaultEasing;

		// Cancel previous animation if any
		if (activeFrame !== null) {
			cancelAnimationFrame(activeFrame);
			activeFrame = null;
		}

		const startY =
			window.pageYOffset || document.documentElement.scrollTop || 0;
		const scrollHeight =
			document.documentElement.scrollHeight || document.body.scrollHeight || 0;
		const maxY = Math.max(0, scrollHeight - window.innerHeight);
		const clampedTarget = Math.min(Math.max(targetY, 0), maxY);
		const distance = clampedTarget - startY;

		if (Math.abs(distance) < 1 || duration === 0) {
			window.scrollTo(0, clampedTarget);
			return;
		}

		const startTime = performance.now();

		function step(currentTime) {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / duration, 1);
			const easedProgress = easing(progress);
			window.scrollTo(0, startY + distance * easedProgress);

			if (progress < 1) {
				activeFrame = requestAnimationFrame(step);
			} else {
				activeFrame = null;
			}
		}

		activeFrame = requestAnimationFrame(step);
	};
})();

/**
 * Scroll helper that chooses between instant scroll and animated scroll.
 * @param {number} targetY - Scroll destination in pixels
 * @param {'smooth'|'auto'} behavior - Desired scroll behavior
 * @param {Object} [options] - Additional animation options
 */
function scrollWindowTo(targetY, behavior = "smooth", options = {}) {
	if (behavior === "smooth") {
		animateWindowScrollTo(targetY, options);
	} else {
		window.scrollTo(0, Math.max(0, targetY));
	}
}

/**
 * Initialize scroll trigger to sync visual navigation with page sections
 * - Tracks which section is currently visible
 * - Updates active state on nav items
 * - Handles click-to-scroll navigation
 * @returns {void}
 */
function initScrollTrigger() {
	// avoid double init
	if (document.documentElement.dataset.scrollTriggerInit === "1") return;
	document.documentElement.dataset.scrollTriggerInit = "1";

	// guard: library and target must exist
	if (typeof ScrollTrigger !== "function") return;
	if (!document.querySelector(".collection-header")) return;

	// get all visual nav items
	const navItems = document.querySelectorAll("[data-visual-nav-item]");

	// create ScrollTrigger instance to watch collection sections
	// triggers when sections cross the 50% viewport threshold
	const trigger = new ScrollTrigger({
		sections: "[data-collection-header]",
		offset: "50%",
		onIndexChange: ({ currentIndex, currentElement }) => {
			// update active state on nav items
			navItems.forEach((item) => item.classList.remove("active"));

			if (navItems[currentIndex]) {
				const activeItem = navItems[currentIndex];
				activeItem.classList.add("active");

				// scroll the active nav item into view (centered in the horizontal container)
				activeItem.scrollIntoView({
					behavior: "smooth",
					block: "nearest",
					inline: "center",
				});
			}
		},
	});

	/**
	 * Scroll to a specific section with proper offset calculation
	 * Accounts for all sticky elements (header, announcement bar, visual nav)
	 */
	function scrollToSection(index) {
		const sections = trigger.getElements();
		const targetSection = sections[index];

		if (!targetSection) return;

		// get elements
		const visualNav = document.querySelector("[data-visual-nav]");
		const header = document.querySelector("#header");
		const announcementBar = document.querySelector(".announcement-bar");

		// calculate heights
		const headerHeight = header ? header.offsetHeight : 0;
		const announcementHeight =
			announcementBar && announcementBar.offsetHeight > 0
				? announcementBar.offsetHeight
				: 0;
		const visualNavHeight = visualNav ? visualNav.offsetHeight : 0;

		// get current scroll position
		const currentScroll =
			window.pageYOffset || document.documentElement.scrollTop;

		// get section's absolute position in document
		const sectionDocumentTop =
			targetSection.getBoundingClientRect().top + currentScroll;

		// calculate desired viewport offset (where section should appear in viewport)
		// include all sticky elements plus a small buffer
		const viewportOffset =
			headerHeight + announcementHeight + visualNavHeight + 20;

		// calculate target scroll position
		const targetScroll = sectionDocumentTop - viewportOffset;

		// scroll to position
		scrollWindowTo(targetScroll, "smooth", { duration: 600 });

		// The link's default jump is prevented, so focus has to be moved manually or keyboard
		// and screen reader users stay behind in the navigation.
		if (!targetSection.hasAttribute("tabindex")) {
			targetSection.setAttribute("tabindex", "-1");
		}
		targetSection.focus({ preventScroll: true });
	}

	// add click handlers to nav items for smooth scrolling
	navItems.forEach((navItem, index) => {
		navItem.addEventListener("click", (e) => {
			e.preventDefault();
			scrollToSection(index);
		});
	});

	// handle deep linking on page load
	// supports both URL hash (#snack-mix) and URL path (/collections/snack-mix)
	let targetHash = null;

	// check for URL hash first (e.g., /collections/all#snack-mix)
	if (window.location.hash) {
		targetHash = window.location.hash;
	} else {
		// extract collection name from URL path (e.g., /collections/snack-mix)
		const pathSegments = window.location.pathname.split("/").filter(Boolean);
		if (pathSegments.length >= 2 && pathSegments[0] === "collections") {
			targetHash = "#" + pathSegments[1];
		}
	}

	// find matching nav item and scroll to it
	if (targetHash) {
		const matchingNavItem = Array.from(navItems).find(
			(item) => item.getAttribute("href") === targetHash,
		);

		if (matchingNavItem) {
			const index = Array.from(navItems).indexOf(matchingNavItem);

			// delay to ensure page is fully rendered before scrolling
			setTimeout(() => {
				scrollToSection(index);
			}, 100);
		}
	}
}

/**
 * Makes the visual navigation sticky with dynamic offset
 *
 * Strategy: Uses a placeholder element to prevent content shift when nav becomes fixed
 * - When nav becomes fixed (position: fixed), it's removed from document flow
 * - The placeholder takes up the space to prevent everything below from jumping up
 * - When nav returns to normal flow, placeholder height is removed
 *
 * The nav sticks below the header and announcement bar
 */
function initStickyVisualNav() {
	const visualNav = document.querySelector("[data-visual-nav]");
	const placeholder = document.querySelector("[data-visual-nav-placeholder]");
	const header = document.querySelector("#header");
	const announcementBar = document.querySelector(".announcement-bar");

	if (!visualNav || !placeholder) return;

	let headerHeight = 0;
	let announcementHeight = 0;
	let stickyOffset = 0;
	let isSticky = false;

	/**
	 * Calculate the combined height of header and announcement bar
	 * Also stores the nav's original position for threshold calculation
	 */
	function calculateOffsets() {
		headerHeight = header ? header.offsetHeight : 0;
		announcementHeight =
			announcementBar && announcementBar.offsetHeight > 0
				? announcementBar.offsetHeight
				: 0;
		stickyOffset = headerHeight + announcementHeight;

		// store original position (only once, on first calculation)
		if (!visualNav.dataset.originalTop) {
			const rect = visualNav.getBoundingClientRect();
			const scrollTop =
				window.pageYOffset || document.documentElement.scrollTop;
			visualNav.dataset.originalTop = rect.top + scrollTop;
		}
	}

	/**
	 * Update sticky state based on scroll position
	 * Manages placeholder height to prevent content shift
	 */
	function updateStickyState() {
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const originalTop = parseInt(visualNav.dataset.originalTop || 0);

		// calculate when nav should become sticky
		// it sticks when its top edge reaches the bottom of header + announcement
		const stickyThreshold = originalTop - stickyOffset;

		if (scrollTop >= stickyThreshold) {
			if (!isSticky) {
				isSticky = true;
				// set placeholder height to prevent content shift
				placeholder.style.height = visualNav.offsetHeight + "px";
				visualNav.classList.add("is-fixed");
				visualNav.style.top = stickyOffset + "px";
			}
		} else {
			if (isSticky) {
				isSticky = false;
				// remove placeholder height to restore normal flow
				placeholder.style.height = "";
				visualNav.classList.remove("is-fixed");
				visualNav.style.top = "";
			}
		}
	}

	/**
	 * Handle window resize by resetting and recalculating positions
	 */
	function handleResize() {
		// reset to initial state
		delete visualNav.dataset.originalTop;
		isSticky = false;
		placeholder.style.height = "";
		visualNav.classList.remove("is-fixed");
		visualNav.style.top = "";

		// recalculate everything
		calculateOffsets();
		updateStickyState();
	}

	// initialize on page load
	calculateOffsets();
	updateStickyState();

	// ============================================================
	// Event Listeners
	// ============================================================

	// scroll listener with requestAnimationFrame for smooth performance
	let scrollTimeout;
	window.addEventListener(
		"scroll",
		function () {
			if (scrollTimeout) {
				window.cancelAnimationFrame(scrollTimeout);
			}
			scrollTimeout = window.requestAnimationFrame(function () {
				updateStickyState();
			});
		},
		{ passive: true },
	);

	// window resize listener (debounced to avoid excessive recalculations)
	let resizeTimeout;
	window.addEventListener("resize", function () {
		clearTimeout(resizeTimeout);
		resizeTimeout = setTimeout(handleResize, 150);
	});

	// ResizeObserver to detect height changes in header/announcement bar
	// (e.g., when announcement bar is shown/hidden dynamically)
	if (typeof ResizeObserver !== "undefined") {
		const resizeObserver = new ResizeObserver(function () {
			calculateOffsets();
			if (isSticky) {
				visualNav.style.top = stickyOffset + "px";
			}
		});

		if (header) resizeObserver.observe(header);
		if (announcementBar) resizeObserver.observe(announcementBar);
	}
}

// Run when DOM is ready
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initStickyVisualNav, {
		once: true,
	});
} else {
	initStickyVisualNav();
}

document.addEventListener(
	"DOMContentLoaded",
	() => {
		setTimeout(() => {
			initScrollTrigger();
		}, 150);
	},
	{
		once: true,
	},
);
